/**
 * modules/fvNewsCarousel.js
 * FV専用 NEWS カルーセル制御
 *
 * - 1040px 以上のみ動作（リサイズで再判定）
 * - 無限ループ：first / last クローンを両端に追加
 * - 自動送り 5秒（hover / focusin / visibilitychange で一時停止）
 * - prefers-reduced-motion 対応（アニメーションなし・短縮タイマー）
 */

import { prefersReducedMotion } from "../core/prefers-reduced-motion.js";

const PC_MIN      = 1040;   // px
const DURATION    = 700;    // slide transition ms
const AUTO_MS     = 5000;   // autoplay interval ms
const EASING      = "cubic-bezier(0.4, 0, 0.2, 1)";

export function initFvNewsCarousel() {
  const wrap = document.querySelector(".js-fv-news-carousel");
  if (!wrap) return;

  const track    = wrap.querySelector(".js-fvnews-track");
  const dotsNav  = wrap.querySelector(".js-fvnews-dots");
  const prevBtn  = wrap.querySelector(".js-fvnews-prev");
  const nextBtn  = wrap.querySelector(".js-fvnews-next");
  const viewport = wrap.querySelector(".p-fvNews__viewport");

  if (!track) return;

  const origItems = Array.from(track.querySelectorAll(".js-fvnews-item"));
  const count     = origItems.length;
  if (count < 1) return;

  const dotBtns   = dotsNav ? Array.from(dotsNav.querySelectorAll(".js-fvnews-dot")) : [];
  const reduced   = prefersReducedMotion();

  // ── クローン追加（無限ループ用） ───────────────────────────
  // トラック構成: [clone_last, item_0…item_N-1, clone_first]
  // trackIndex:   [     0,       1…N,              N+1     ]
  if (count >= 2) {
    const clLast  = origItems[count - 1].cloneNode(true);
    const clFirst = origItems[0].cloneNode(true);
    [clLast, clFirst].forEach((cl) => {
      cl.setAttribute("aria-hidden", "true");
      cl.querySelectorAll("a").forEach((a) => a.setAttribute("tabindex", "-1"));
    });
    track.insertBefore(clLast, origItems[0]);
    track.appendChild(clFirst);
  }

  // クローン追加後の全スライド
  const allSlides = Array.from(track.querySelectorAll(".js-fvnews-item"));

  // ── 状態変数 ──────────────────────────────────────────────
  let realIdx    = 0;                      // 0-based（ドット・aria用）
  let trackIdx   = count >= 2 ? 1 : 0;   // track内インデックス
  let isAnim     = false;
  let isPaused   = false;
  let autoTimer  = null;
  let pcActive   = window.innerWidth >= PC_MIN;

  // ── helpers ────────────────────────────────────────────────

  function slideOffset(tIdx) {
    // offsetLeft は整数丸めで誤差が出るため、getBoundingClientRect の差分で計算する
    // slide.left - track.left はトランスフォームをキャンセルし合うので
    // 「トラック内スライドの自然オフセット」を小数精度で取得できる
    if (!track || !allSlides[tIdx]) return 0;
    const trackLeft = track.getBoundingClientRect().left;
    const slideLeft = allSlides[tIdx].getBoundingClientRect().left;
    return -(slideLeft - trackLeft);
  }

  function applyTransform(tIdx, animate) {
    if (reduced || !animate) {
      track.style.transition = "none";
    } else {
      track.style.transition = `transform ${DURATION}ms ${EASING}`;
    }
    track.style.transform = `translateX(${slideOffset(tIdx)}px)`;
  }

  function updateDots(rIdx) {
    dotBtns.forEach((btn, i) => {
      const active = i === rIdx;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-current", active ? "true" : "false");
    });
  }

  function updateAria(rIdx) {
    origItems.forEach((item, i) => {
      const active = i === rIdx;
      item.setAttribute("aria-hidden", active ? "false" : "true");
      item.querySelectorAll("a").forEach((a) =>
        a.setAttribute("tabindex", active ? "0" : "-1")
      );
    });
  }

  // 指定 realIdx へジャンプ（アニメあり/なし）
  function goTo(rIdx, animate) {
    realIdx  = rIdx;
    trackIdx = count >= 2 ? rIdx + 1 : rIdx;
    applyTransform(trackIdx, animate);
    updateDots(rIdx);
    updateAria(rIdx);
  }

  // ── transitionend：クローン位置からリアル位置へ無音ジャンプ ──
  track.addEventListener("transitionend", (e) => {
    if (e.target !== track) return;
    isAnim = false;

    if (count < 2) return;

    if (trackIdx >= count + 1) {
      // clone_first → 本物 first (trackIdx=1)
      track.style.transition = "none";
      trackIdx = 1;
      realIdx  = 0;
      track.style.transform = `translateX(${slideOffset(1)}px)`;
    } else if (trackIdx <= 0) {
      // clone_last → 本物 last (trackIdx=N)
      track.style.transition = "none";
      trackIdx = count;
      realIdx  = count - 1;
      track.style.transform = `translateX(${slideOffset(count)}px)`;
    }
  });

  // ── 送り操作 ──────────────────────────────────────────────

  function next() {
    if (isAnim && !reduced) return;
    isAnim = true;
    trackIdx++;
    realIdx = (realIdx + 1) % count;
    applyTransform(trackIdx, true);
    updateDots(realIdx);
    updateAria(realIdx);
    if (reduced) isAnim = false;
  }

  function prev() {
    if (isAnim && !reduced) return;
    isAnim = true;
    trackIdx--;
    realIdx = (realIdx - 1 + count) % count;
    applyTransform(trackIdx, true);
    updateDots(realIdx);
    updateAria(realIdx);
    if (reduced) isAnim = false;
  }

  // ── オートプレイ ───────────────────────────────────────────

  function startAuto() {
    stopAuto();
    if (count < 2 || reduced || !pcActive) return;
    autoTimer = setInterval(() => {
      if (!isPaused && !document.hidden) next();
    }, AUTO_MS);
  }

  function stopAuto() {
    if (autoTimer) {
      clearInterval(autoTimer);
      autoTimer = null;
    }
  }

  function resetAuto() {
    stopAuto();
    if (!isPaused) startAuto();
  }

  // ── イベント：矢印・ドット ──────────────────────────────────

  if (prevBtn) {
    prevBtn.addEventListener("click", () => { prev(); resetAuto(); });
  }
  if (nextBtn) {
    nextBtn.addEventListener("click", () => { next(); resetAuto(); });
  }

  dotBtns.forEach((btn, i) => {
    btn.addEventListener("click", () => {
      if (i === realIdx) return;
      // ドット選択は直接ジャンプ（遠距離でも即時、隣接は短アニメ）
      const animate = Math.abs(i - realIdx) <= 1;
      goTo(i, animate);
      resetAuto();
    });
  });

  // ── ホバー・フォーカスで一時停止 ──────────────────────────

  const carouselArea = wrap;

  carouselArea.addEventListener("mouseenter", () => { isPaused = true;  stopAuto(); });
  carouselArea.addEventListener("mouseleave", () => { isPaused = false; startAuto(); });

  carouselArea.addEventListener("focusin",  () => { isPaused = true;  stopAuto(); });
  carouselArea.addEventListener("focusout", (e) => {
    if (!carouselArea.contains(e.relatedTarget)) {
      isPaused = false;
      startAuto();
    }
  });

  // ── タブ非表示で一時停止 ──────────────────────────────────

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) { stopAuto(); } else { if (!isPaused) startAuto(); }
  });

  // ── リサイズ対応 ──────────────────────────────────────────

  let resizeTimer = null;
  window.addEventListener(
    "resize",
    () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasPC = pcActive;
        pcActive = window.innerWidth >= PC_MIN;
        // 位置ずれ補正
        applyTransform(trackIdx, false);
        if (pcActive && !wasPC) {
          startAuto();
        } else if (!pcActive) {
          stopAuto();
        }
      }, 150);
    },
    { passive: true }
  );

  // ── 初期配置・スタート ─────────────────────────────────────

  goTo(0, false);

  if (pcActive) startAuto();
}
